import { FormSelect } from "../../../../components/ui/Input/FormSelector/FormSelect";
import { Card, CardContent, CardHeader } from "../../../../components/ui/common/card";
import { ToggleGroup, ToggleGroupItem } from "../../../../components/ui/common/toggle-group";
import { IngredientRegisterViewModel } from "../types/ingredientRegisterView.types";
import EnvironmentalDataProcessedFields from "./EnvironmentalDataProcessedFields";
import EnvironmentalDataRawFields from "./EnvironmentalDataRawFields";

interface EnvironmentalDataCardProps {
  vm: IngredientRegisterViewModel;
}

const TOGGLE_ON_CLASS =
  "data-[state=on]:tw-bg-primary data-[state=on]:tw-text-primary-foreground data-[state=on]:hover:tw-bg-primary";

/**
 * "ENVIRONMENTAL DATA" card — Yes/No toggle (a two-button `ToggleGroup`
 * rather than a sliding `Switch`, matching the reference screenshot's
 * segmented control look) plus Ingredient Type/Data Type, then the
 * RAW-vs-PROCESSED field group driven by `derivePackageFlags`. Select fields
 * use the shared `FormSelect` (src/components/ui/Input/FormSelector/),
 * rendered inside the `RegisterFormProvider` wrapper in
 * `IngredientRegisterModal.tsx`.
 */
const EnvironmentalDataCard = ({ vm }: EnvironmentalDataCardProps) => {
  if (!vm.showUnitDetails || !vm.isEnableEnvironmentData) return null;

  return (
    <Card className="tw-gap-3 tw-py-4">
      <CardHeader className="tw-px-4">
        <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted-foreground">
          Environmental Data
        </span>
      </CardHeader>
      <CardContent className="tw-flex tw-flex-col tw-gap-3 tw-px-4">
        <div className="tw-flex tw-items-center tw-justify-between tw-rounded-md tw-border tw-border-border tw-bg-primary/5 tw-px-3 tw-py-2">
          <p className="tw-text-sm tw-font-medium tw-text-primary">
            Do you want to add environmental data?
          </p>
          <ToggleGroup
            type="single"
            value={vm.viewEnvironmentalData ? "yes" : "no"}
            onValueChange={value => {
              if (value) vm.onToggleEnvironmentalData(value === "yes");
            }}
          >
            <ToggleGroupItem value="no" className={TOGGLE_ON_CLASS}>
              No
            </ToggleGroupItem>
            <ToggleGroupItem value="yes" className={TOGGLE_ON_CLASS}>
              Yes
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        {vm.viewEnvironmentalData ? (
          <>
            <div className="tw-grid tw-grid-cols-1 tw-gap-3 md:tw-grid-cols-2">
              <FormSelect
                control={vm.control}
                name="ingredient_type"
                label="Ingredient Type"
                options={vm.ingredientTypeOptions}
                placeholder="Ingredient Type"
                onChange={vm.onIngredientTypeChange}
              />
              <FormSelect
                control={vm.control}
                name="data_type"
                label="Data Type"
                options={vm.dataTypeOptions}
                placeholder="Select Type"
                onChange={vm.onDataTypeChange}
              />
            </div>

            {vm.showRawFields ? <EnvironmentalDataRawFields vm={vm} /> : null}
            {vm.showProcessedFields ? <EnvironmentalDataProcessedFields vm={vm} /> : null}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default EnvironmentalDataCard;
